import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  currentDate: new Date().toISOString(),
  viewMode: 'week', // 'day', 'week', 'month'
  selectedAppointment: null,
  isModalOpen: false,
  isSettingsOpen: false,
  settingsTab: 'workHours', // 'workHours', 'booking', 'sms'
  roomFilterId: null, // null = all rooms; room id = show only that room's appointments
  showRoomAvailability: false, // paint the filtered room's open hours on the grid
};

// Month step that keeps the day-of-month but never overflows (Jan 31 + 1 month
// used to land on Mar 3, skipping February). Clamps to the last day instead.
const shiftMonth = (date, delta) => {
  const day = date.getDate();
  date.setDate(1);
  date.setMonth(date.getMonth() + delta);
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  date.setDate(Math.min(day, lastDay));
};

const calendarSlice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    setCurrentDate: (state, action) => {
      state.currentDate = action.payload;
    },
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
    goToToday: (state) => {
      state.currentDate = new Date().toISOString();
    },
    goToPrevious: (state) => {
      const current = new Date(state.currentDate);
      if (state.viewMode === 'day') {
        current.setDate(current.getDate() - 1);
      } else if (state.viewMode === 'week') {
        current.setDate(current.getDate() - 7);
      } else {
        shiftMonth(current, -1);
      }
      state.currentDate = current.toISOString();
    },
    goToNext: (state) => {
      const current = new Date(state.currentDate);
      if (state.viewMode === 'day') {
        current.setDate(current.getDate() + 1);
      } else if (state.viewMode === 'week') {
        current.setDate(current.getDate() + 7);
      } else {
        shiftMonth(current, 1);
      }
      state.currentDate = current.toISOString();
    },
    selectAppointment: (state, action) => {
      state.selectedAppointment = action.payload;
    },
    openModal: (state, action) => {
      state.isModalOpen = true;
      if (action.payload) {
        state.selectedAppointment = action.payload;
      }
    },
    closeModal: (state) => {
      state.isModalOpen = false;
      state.selectedAppointment = null;
    },
    openSettings: (state, action) => {
      state.isSettingsOpen = true;
      if (action.payload) {
        state.settingsTab = action.payload;
      }
    },
    closeSettings: (state) => {
      state.isSettingsOpen = false;
    },
    setSettingsTab: (state, action) => {
      state.settingsTab = action.payload;
    },
    setRoomFilter: (state, action) => {
      state.roomFilterId = action.payload || null;
      if (!state.roomFilterId) state.showRoomAvailability = false;
    },
    setShowRoomAvailability: (state, action) => {
      state.showRoomAvailability = action.payload === true;
    },
  },
});

export const {
  setCurrentDate,
  setViewMode,
  goToToday,
  goToPrevious,
  goToNext,
  selectAppointment,
  openModal,
  closeModal,
  openSettings,
  closeSettings,
  setSettingsTab,
  setRoomFilter,
  setShowRoomAvailability,
} = calendarSlice.actions;

export const selectCurrentDate = (state) => state.calendar.currentDate;
export const selectViewMode = (state) => state.calendar.viewMode;
export const selectSelectedAppointment = (state) => state.calendar.selectedAppointment;
export const selectIsModalOpen = (state) => state.calendar.isModalOpen;
export const selectIsSettingsOpen = (state) => state.calendar.isSettingsOpen;
export const selectSettingsTab = (state) => state.calendar.settingsTab;
export const selectRoomFilterId = (state) => state.calendar.roomFilterId;
export const selectShowRoomAvailability = (state) => state.calendar.showRoomAvailability;

export default calendarSlice.reducer;

